import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities';
import { fg } from '@confluence/feature-gating';

declare global {
	interface Window {
		__dataFetcherForSuspense__?: Record<string, SuspenseableDataFetcher>;
	}
}

let mockSuspenseDelayAccumulated = 0;

type PromiseState = 'pending' | 'resolved' | 'error';

export type SuspenseableDataFetcher = {
	getError: () => Error | null;
	read: () => any;
};

export function getDataFetcherForSuspense(
	dataFetcherName: string,
	isSSRStreaming: boolean,
): SuspenseableDataFetcher | null {
	if (!isSSRStreaming) {
		return null;
	}
	return window.__dataFetcherForSuspense__?.[dataFetcherName] ?? null;
}

/**
 * Same interface as Promise.all but wait for all promises to settle.
 */
export async function allSettled<T>(promises: Promise<T>[]): Promise<T[]> {
	if (fg('query_preloader_no_promise_all')) {
		return Promise.allSettled(promises).then((results) => {
			const errors = results
				.filter((result): result is PromiseRejectedResult => result.status === 'rejected')
				.map((result) => result.reason);

			// Keep the same interface as Promise.all putting all other errors in otherErrors field
			if (errors.length > 0) {
				const error = errors[0];
				if (errors.length > 1) {
					error.otherErrors = errors.slice(1);
				}
				throw error;
			}
			return results
				.filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
				.map((result) => result.value);
		});
	}
	return Promise.all(promises);
}

export function createDataFetcherForSuspense(
	dataFetcherName: string,
	promise: Promise<any> | Promise<any>[] | null,
) {
	promise = promise ?? Promise.resolve();

	if (Array.isArray(promise)) {
		promise = allSettled(promise);
	}

	if (!window.__SSR_REACT_STREAMING__) {
		return promise;
	}

	if (!window.__dataFetcherForSuspense__) {
		window.__dataFetcherForSuspense__ = {};
	}

	const dataFetcher = window.__dataFetcherForSuspense__[dataFetcherName];
	if (!dataFetcher) {
		const urlParams = new URLSearchParams(window.location.search);
		const mockDelay = urlParams.get('MOCK_SUSPENSE_QUERYDELAY');
		// If MOCK_SUSPENSE_QUERYDELAY = 1000, we want first data fetcher to have delay of 1000ms then second one to have 2000ms
		// In this case, we can see the suspense is really working. They should appear piece by piece.
		mockSuspenseDelayAccumulated += mockDelay ? parseInt(mockDelay) : 0;

		window.__dataFetcherForSuspense__[dataFetcherName] = createDataFetcher(
			dataFetcherName,
			promise,
			mockSuspenseDelayAccumulated,
		);
	}

	return Promise.resolve();
}

// export for testing
export function resetDelay() {
	mockSuspenseDelayAccumulated = 0;
}

// export for testing
export function createDataFetcher(
	dataFetcherName: string,
	promise: Promise<any>,
	delay: number = 0,
): SuspenseableDataFetcher {
	let dataResult: any = null;
	let errorResult: any = null;
	let promiseState: PromiseState = 'pending';

	const suspender = new Promise<void>((resolve) => {
		promise
			.then((result) => {
				if (delay > 0) {
					setTimeout(() => {
						dataResult = result;
						promiseState = 'resolved';
						resolve();
					}, delay);
				} else {
					dataResult = result;
					promiseState = 'resolved';
					resolve();
				}
			})
			.catch((error) => {
				errorResult = error;
				promiseState = 'error';

				// When encountered multiple errors, errors other than the first one are stored in otherErrors field.
				const errors = [error].concat(error.otherErrors || []);
				for (const err of errors) {
					if (process.env.NODE_ENV !== 'production') {
						// eslint-disable-next-line no-console
						console.error(
							`Error when preloading query for data fetcher ${dataFetcherName}:`,
							err.stack,
						);
					}
					// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
					if (process.env.REACT_SSR) {
						ssrPrepareErrorsClient.submitError(err, { dataFetcherName });
					}
				}

				resolve();
			});
	});

	return {
		getError() {
			return errorResult;
		},
		read() {
			if (promiseState === 'pending') {
				throw suspender;
			} else if (promiseState === 'error') {
				// Data fetcher is a wrapper of preloader. If an error happened we don't want to blow up the tree too early.
				// Return null here to signal the Suspense to continue.
				// The normal error handling in the component will take care of it.
				return null;
			} else if (promiseState === 'resolved') {
				return dataResult;
			}
		},
	};
}
